import React, { useState, useEffect } from 'react';

function TicketReplyPanel({ ticket, communications, onSendReply }) {
  const [emailData, setEmailData] = useState({
    subject: '',
    cc: [],
    body: '',
    attachments: []
  });

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [newCC, setNewCC] = useState('');
  const [sending, setSending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewingEmail, setViewingEmail] = useState(null);
  const itemsPerPage = 5;

  const templates = [
    { id: 'none', name: 'Select a template...', content: '' },
    { id: 'initial_response', name: 'Initial Response', content: 'Dear {requester_name},\n\nThank you for contacting HR. We have received your request and are currently reviewing it.\n\nTicket Number: {ticket_number}\n\nWe will get back to you within 24-48 hours.\n\nBest regards,\nHR Team' },
    { id: 'request_info', name: 'Request More Information', content: 'Dear {requester_name},\n\nThank you for your request. To better assist you, we need some additional information:\n\n- [Please list specific information needed]\n\nPlease reply to this email with the requested details.\n\nBest regards,\nHR Team' },
    { id: 'resolved', name: 'Ticket Resolved', content: 'Dear {requester_name},\n\nYour request (Ticket #{ticket_number}) has been resolved.\n\n[Please describe the resolution]\n\nIf you have any further questions, please don\'t hesitate to reach out.\n\nBest regards,\nHR Team' }
  ];

  useEffect(() => {
    if (ticket) {
      setEmailData(prev => ({
        ...prev,
        subject: `RE: ${ticket.ticket_number} - ${ticket.category_name || 'HR Request'}`
      }));
    }
  }, [ticket]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template && template.content) {
      let content = template.content;
      content = content.replace('{requester_name}', ticket?.requester_name || 'there');
      content = content.replace(/{ticket_number}/g, ticket?.ticket_number || '');
      setEmailData(prev => ({ ...prev, body: content }));
    }
  };

  const handleAddCC = () => {
    if (newCC && !emailData.cc.includes(newCC) && newCC.includes('@')) {
      setEmailData(prev => ({
        ...prev,
        cc: [...prev.cc, newCC]
      }));
      setNewCC('');
    }
  };

  const handleRemoveCC = (email) => {
    setEmailData(prev => ({
      ...prev,
      cc: prev.cc.filter(e => e !== email)
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.size <= 10 * 1024 * 1024); // 10MB max
    setEmailData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));
  };

  const handleRemoveAttachment = (index) => {
    setEmailData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await onSendReply({
        ...emailData,
        created_by: localStorage.getItem('userEmail') || 'hr@cafuamanagement.com',
        sent_at: new Date().toISOString()
      });
      // Reset form
      setEmailData({
        subject: `RE: ${ticket.ticket_number} - ${ticket.category_name || 'HR Request'}`,
        cc: [],
        body: '',
        attachments: []
      });
      setSelectedTemplate('');
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pagination
  const paginatedComms = communications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(communications.length / itemsPerPage);

  if (!ticket) return null;

  return (
    <div className="ticket-reply-panel">
      {/* Email Composer Section */}
      <div className="reply-composer-section">
        <h3>Reply to Ticket</h3>

        {/* Template Dropdown */}
        <div className="form-group">
          <label>Template</label>
          <select
            value={selectedTemplate}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            className="control-select"
          >
            {templates.map(template => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </select>
        </div>

        {/* Subject */}
        <div className="form-group">
          <label>Subject</label>
          <input
            type="text"
            value={emailData.subject}
            onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
            className="control-input"
            readOnly
          />
        </div>

        {/* CC Field */}
        <div className="form-group">
          <label>CC</label>
          <div className="cc-chips">
            {emailData.cc.map((email, index) => (
              <span key={index} className="cc-chip">
                {email}
                <button onClick={() => handleRemoveCC(email)} className="chip-remove">×</button>
              </span>
            ))}
          </div>
          <div className="cc-input-wrapper">
            <input
              type="email"
              value={newCC}
              onChange={(e) => setNewCC(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddCC()}
              placeholder="Add CC email..."
              className="control-input"
            />
            <button onClick={handleAddCC} className="btn-add-cc">Add</button>
          </div>
        </div>

        {/* Rich Text Editor (simplified) */}
        <div className="form-group">
          <label>Message</label>
          <div className="editor-toolbar">
            <button type="button" className="toolbar-btn" title="Bold"><strong>B</strong></button>
            <button type="button" className="toolbar-btn" title="Italic"><em>I</em></button>
            <button type="button" className="toolbar-btn" title="Underline"><u>U</u></button>
            <button type="button" className="toolbar-btn" title="Bullet List">• List</button>
          </div>
          <textarea
            value={emailData.body}
            onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
            className="control-textarea"
            rows="8"
            placeholder="Type your message here..."
          />
        </div>

        {/* Attachments */}
        <div className="form-group">
          <label>Attachments</label>
          <input
            type="file"
            onChange={handleFileUpload}
            multiple
            style={{ display: 'none' }}
            id="file-upload"
          />
          <label htmlFor="file-upload" className="btn-add-attachment">
            📎
          </label>
          {emailData.attachments.length > 0 && (
            <div className="attachments-list">
              {emailData.attachments.map((file, index) => (
                <div key={index} className="attachment-item">
                  <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                  <button onClick={() => handleRemoveAttachment(index)} className="attachment-remove">×</button>
                </div>
              ))}
            </div>
          )}
          <small className="help-text">Max 10MB per file, multiple files allowed</small>
        </div>

        {/* Send Button */}
        <div className="form-actions">
          <button
            onClick={handleSend}
            disabled={sending || !emailData.body}
            className="btn-primary"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>

      {/* Previous Communications Section */}
      <div className="previous-communications-section">
        <h3>Previous Communications</h3>

        {communications.length === 0 ? (
          <div className="empty-state">
            <p>No previous communications</p>
          </div>
        ) : (
          <>
            <table className="communications-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Created By</th>
                  <th>Sent At</th>
                  <th>Replied At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedComms.map((comm, index) => (
                  <tr key={index}>
                    <td className="email-content-cell">
                      {(comm.comment || comm.email_content || '').substring(0, 50)}...
                    </td>
                    <td>{comm.created_by || 'Unknown'}</td>
                    <td>{formatDate(comm.sent_at || comm.created_at)}</td>
                    <td>{formatDate(comm.replied_at)}</td>
                    <td>
                      <button
                        onClick={() => setViewingEmail(comm)}
                        className="btn-view-email"
                        title="View full email"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="btn-pagination"
                >
                  Previous
                </button>
                <span className="page-info">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-pagination"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Modal */}
      {viewingEmail && (
        <div className="modal-overlay" onClick={() => setViewingEmail(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Email Details</h2>
              <button onClick={() => setViewingEmail(null)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>From:</strong> {viewingEmail.created_by}</p>
              <p><strong>Sent:</strong> {formatDate(viewingEmail.sent_at || viewingEmail.created_at)}</p>
              <hr />
              <div className="email-full-content">
                {viewingEmail.comment || viewingEmail.email_content || 'No content'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketReplyPanel;
